import React, { useState, useEffect } from "react";
import { Wallet as WalletIcon, TrendingUp, Coins, Gift, ArrowDownToLine, Clock, Check, X, Loader2 } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { toast } from "sonner";
import { Progress } from "@/components/ui/progress";

const STATUS_COLORS = { pending: "text-amber-400", approved: "text-blue-400", processing: "text-cyan-400", completed: "text-green-400", rejected: "text-rose-400" };

export default function WalletPage() {
  const [data, setData] = useState(null);
  const [txs, setTxs] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [amount, setAmount] = useState(5);
  const [method, setMethod] = useState("stripe");
  const [dest, setDest] = useState("");
  const [loading, setLoading] = useState(false);

  const load = () => {
    api.get("/wallet").then((r) => { setData(r.data); setAmount(r.data.min_withdrawal); }).catch(() => {});
    api.get("/wallet/transactions").then((r) => setTxs(r.data.transactions)).catch(() => {});
    api.get("/withdrawals").then((r) => setWithdrawals(r.data.withdrawals)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  if (!data) return <div className="grid place-items-center py-24"><Loader2 className="animate-spin text-purple-400" /></div>;
  const w = data.wallet;
  const fee = data.withdrawal_fee;
  const net = Math.max(0, (Number(amount) || 0) - fee);

  const requestWithdraw = async () => {
    setLoading(true);
    try { const { data: r } = await api.post("/withdrawals", { amount: Number(amount), method, destination: dest }); toast.success(`Requested $${r.amount.toFixed(2)} — net $${r.net.toFixed(2)} after $${r.fee.toFixed(2)} fee`); load(); }
    catch (e) { toast.error(errMsg(e)); }
    setLoading(false);
  };

  const buckets = [
    { label: "Listening earnings", value: w.listening_earnings, icon: Coins },
    { label: "Creator earnings", value: w.creator_earnings, icon: TrendingUp },
    { label: "Tips received", value: w.tips_received, icon: Gift },
    { label: "Withdrawn", value: w.withdrawn, icon: ArrowDownToLine },
  ];

  return (
    <div className="px-4 sm:px-6 lg:px-8 pt-4 lg:pt-6 max-w-4xl mx-auto">
      <h1 className="text-2xl font-bold font-display flex items-center gap-2 mb-5"><WalletIcon className="text-purple-400" /> My Wallet</h1>

      <div className="jx-radial-bloom border border-white/10 rounded-3xl p-6 mb-5" data-testid="wallet-balance-card">
        <p className="text-xs font-mono uppercase tracking-wider text-slate-400">Available to withdraw</p>
        <p className="text-4xl font-black font-display mt-1" data-testid="wallet-available">${Number(w.available_balance).toFixed(2)}</p>
        <div className="flex gap-6 mt-3 text-sm">
          <span className="text-slate-400">Lifetime: <b className="text-slate-200">${Number(w.lifetime_earnings).toFixed(2)}</b></span>
          <span className="text-slate-400">Pending: <b className="text-slate-200">${Number(w.pending_balance).toFixed(2)}</b></span>
        </div>
        <div className="mt-5">
          <div className="flex justify-between text-xs mb-1.5"><span className="text-slate-400">{data.milestone_label}</span><span className="text-purple-400 font-semibold">{data.progress}%</span></div>
          <Progress value={data.progress} className="h-2.5" data-testid="earnings-progress" />
          <p className="text-[11px] text-slate-500 mt-1.5">${Number(w.available_balance).toFixed(2)} of ${Number(data.earnings_goal).toFixed(2)} goal</p>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        {buckets.map((b) => (
          <div key={b.label} className="jx-card rounded-2xl p-4">
            <b.icon size={16} className="text-cyan-400 mb-2" />
            <p className="text-lg font-bold">${Number(b.value || 0).toFixed(2)}</p>
            <p className="text-xs text-slate-500">{b.label}</p>
          </div>
        ))}
      </div>

      <div className="grid md:grid-cols-2 gap-5">
        <div className="jx-card rounded-2xl p-5">
          <h2 className="font-semibold mb-3 flex items-center gap-2"><ArrowDownToLine size={17} className="text-purple-400" /> Cash Out</h2>
          <p className="text-xs text-slate-400 mb-3">Minimum ${data.min_withdrawal.toFixed(2)}. A ${fee.toFixed(2)} fee applies per completed withdrawal.</p>
          <input type="number" min={data.min_withdrawal} step="0.5" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="withdraw-amount" className="w-full bg-black/30 border border-white/10 rounded-xl px-4 py-2.5 text-sm mb-2" />
          <select value={method} onChange={(e) => setMethod(e.target.value)} className="w-full bg-black/30 border border-white/10 rounded-xl px-4 py-2.5 text-sm mb-2">
            <option value="stripe">Stripe payout</option><option value="paypal">PayPal</option><option value="bank">Bank transfer</option>
          </select>
          <input value={dest} onChange={(e) => setDest(e.target.value)} placeholder="Payout destination (email / account)" className="w-full bg-black/30 border border-white/10 rounded-xl px-4 py-2.5 text-sm mb-3" />
          <div className="text-xs text-slate-400 space-y-1 mb-3 bg-black/20 rounded-lg p-3">
            <div className="flex justify-between"><span>Withdrawal</span><span>${(Number(amount) || 0).toFixed(2)}</span></div>
            <div className="flex justify-between"><span>Fee</span><span>-${fee.toFixed(2)}</span></div>
            <div className="flex justify-between font-semibold text-slate-200"><span>Net payout</span><span data-testid="withdraw-net">${net.toFixed(2)}</span></div>
          </div>
          <button onClick={requestWithdraw} disabled={loading || Number(amount) < data.min_withdrawal} data-testid="withdraw-btn" className="w-full jx-cta text-white font-semibold py-2.5 rounded-xl disabled:opacity-50">{loading ? "Submitting…" : "Request Withdrawal"}</button>
        </div>

        <div className="jx-card rounded-2xl p-5">
          <h2 className="font-semibold mb-3 flex items-center gap-2"><Clock size={17} className="text-cyan-400" /> Withdrawal History</h2>
          <div className="space-y-2 max-h-72 overflow-y-auto">
            {withdrawals.length === 0 && <p className="text-sm text-slate-500">No withdrawals yet.</p>}
            {withdrawals.map((x) => (
              <div key={x.id} className="flex items-center justify-between text-sm p-2.5 rounded-lg bg-black/20">
                <div><p className="font-medium">${x.amount.toFixed(2)} <span className="text-xs text-slate-500">→ net ${x.net.toFixed(2)}</span></p><p className="text-[11px] text-slate-500">{new Date(x.created_at).toLocaleDateString()}</p></div>
                <span className={`text-xs font-semibold capitalize ${STATUS_COLORS[x.status] || "text-slate-400"}`}>{x.status}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="jx-card rounded-2xl p-5 mt-5">
        <h2 className="font-semibold mb-3">Transaction Ledger</h2>
        <div className="space-y-1.5 max-h-80 overflow-y-auto">
          {txs.length === 0 && <p className="text-sm text-slate-500">No transactions yet. Earn by listening to songs for 20+ seconds.</p>}
          {txs.map((t) => (
            <div key={t.id} className="flex items-center justify-between text-sm py-2 border-b border-white/5">
              <div><p className="capitalize">{t.type.replace(/_/g, " ")}</p><p className="text-[11px] text-slate-500">{new Date(t.created_at).toLocaleString()}</p></div>
              <span className={t.direction === "credit" ? "text-green-400" : "text-rose-400"}>{t.direction === "credit" ? "+" : "-"}${t.amount.toFixed(2)}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
